/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import {
    type BaseRequestOptionsArgs, type HttpResponse,
    Consts, DownloadService, HttpService, I18nService, PromptService, UNPLACEHOLDER,
    UtilService,
} from '@dm/core';
import { type License } from 'app/common/types/mo';
import { FeatureConfig } from 'app/config/feature';
import { type Subscriber, Observable } from 'rxjs';

import { GlobalDataService, ToolService } from '../../../common/services';
import { type LicenseFeatureData } from './license-management';

export enum LicenseValue {
    /** NAS基础特性 */
    NasFoundationLicense = '641',
    /** CloudBackup */
    CloudBackupLicense = '101',
    /** X8000 容器备份 */
    DataBackUpCapacity = '648',
    /** 防勒索 */
    AntiRansomwareLicense = '647',
    /**
     * 防勒索 for SAN
     */
    ANTIRANSOMWARE_FOR_SAN = '666',
    /** DBStor客户端 */
    DBStorClient = '657',
    /** 文件升级为文件和对象多协议融合存储 */
    S3 = '649',
    /** 容器ftp/http（原SmartContainer） */
    SmartContainerLicense = '121'
}

@Injectable({ providedIn: 'root' })
export class LicenseManagementService {
    FeatureConfig = FeatureConfig;

    /**
     * 支持license显示已使用容量
     */
    isSupportLicenseUsage = FeatureConfig.IsSupportLicenseUsage;

    i18n = {
        licenseLabel: this.i18nService.get('common.manageLicense_title'),
        noFileTip: this.i18nService.get('settings.licenseManage_desc_label'),
        inActiveTip: this.i18nService.get('settings.licenseManage_term_licenseInactive_label'),
        inActiveInfo: this.i18nService.get('settings.licenseManage_term_goToActive_desc_label'),
        licenseTip: this.i18nService.get('settings.licenseManage_task_checkActivatedLicense_label'),
        licenseNotUploadTip: this.i18nService.get('settings.licenseManage_term_unupload_info'),
        licenseUploadingTip: this.i18nService.get('settings.licenseManage_term_uploadingLicense_tip'),
        liceseUploadErroTip: this.i18nService.get('common.upload_fail_label', [
            this.i18nService.get('settings.licenseManage_task_manageLicense_para_licenseFile_label'),
        ]),
        requestFailedTip: this.i18nService.get('settings.systemSetting_task_caAutoApply_requestFailed_tip'),

        importLicenseBtn: this.i18nService.get('settings.licenseManage_importLicense_label'),
        activeLicenseBtn: this.i18nService.get('settings.licenseManage_term_activeLicense_label'),
        activeBtn: this.i18nService.get('common.active_button'),
        cancelBtn: this.i18nService.get('common.cancel_button'),
        inActiveBtn: this.i18nService.get('settings.licenseManage_term_activeLicense_label'),
        updateBtn: this.i18nService.get('settings.licenseManage_term_updateLicense_button'),
        backupBtn: this.i18nService.get('settings.licenseManage_term_backupLicense_button'),
        saveDifferenceFileBtn: this.i18nService.get('settings.licenseManage_task_saveLicenseCompare_button'),

        licenseFileLab: this.i18nService.get('settings.licenseManage_task_manageLicense_para_licenseFile_label'),
        licenseStatusLab: this.i18nService.get('settings.licenseManage_term_licenseStatus_label'),
        licensePeriodLab: this.i18nService.get('settings.licenseManage_term_LicenseValidityPeriod_label'),
        loadingLab: this.i18nService.get('common.loading_label'),
        licenseSNLab: this.i18nService.get('settings.licenseManage_task_para_licenseSN_label'),
        licenseVersionLab: this.i18nService.get('settings.licenseManage_task_para_licenseVersion_label'),
        createTimeLab: this.i18nService.get('common.createTime_label'),
        descriptionLab: this.i18nService.get('common.description_column', true),
        suggestionLab: this.i18nService.get('common.suggest_label', true),
        confirmLab: this.i18nService.get('common.confirm_lab'),

        uploadErrorResult: this.i18nService.get('common.upload_fail_label', [
            this.i18nService.get('settings.licenseManage_task_manageLicense_para_licenseFile_label'),
        ]),
        activeLicenseMsg: this.i18nService.get('settings.systemSetting_manageLicense_task_activate_msg'),

        featureNameCol: this.i18nService.get('settings.licenseManage_term_FeatureName_column'),
        runningDeadlineCol: this.i18nService.get('settings.licenseManage_term_runningDeadline_column'),
        usedAndTotalCapacityCol: this.i18nService.get('settings.licenseManage_term_usedAndTotalCapacity_column'),
        totalCapacityCol: this.isSupportLicenseUsage ?
            this.i18nService.get('settings.licenseManage_term_usedAndTotalCapacity_column') :
            this.i18nService.get('common.term_totalCapacity_column'),
        activeTimeCol: this.i18nService.get('settings.licenseManage_initialization_term_activeLicense_runTime_tab'),
        inactiveTimeCol: this.i18nService.get('settings.licenseManage_initialization_term_inActiveLicense_runTime_tab'),
        activeCapacityCol: this.isSupportLicenseUsage ?
            this.i18nService.get('settings.licenseManage_initialization_term_activeLicense_capacity_col') :
            this.i18nService.get('settings.licenseManage_initialization_term_activeLicense_capacity_tab'),
        inactiveCapacityCol: this.isSupportLicenseUsage ?
            this.i18nService.get('settings.licenseManage_initialization_term_inActiveLicense_capacity_col') :
            this.i18nService.get('settings.licenseManage_initialization_term_inActiveLicense_capacity_tab'),

        temporaryText: this.i18nService.get('settings.licenseManage_task_para_tem_label'),
        permanentText: this.i18nService.get('settings.licenseManage_task_para_permanent_label'),
        unlimitedText: this.i18nService.get('settings.licenseManage_term_capUnlimited_column'),
        enhancedText: this.i18nService.get('settings.licenseManage_task_compare_enhance_lab'),
        weakenedText: this.i18nService.get('settings.licenseManage_task_compare_weaken_lab'),

        error_not_imported: this.i18nService.get('settings.licenseManage_task_importLicense_info_label'),
        error_not_activated: this.i18nService.get('settings.licenseManage_task_activeLicense_info_label'),

        refreshBrowserTip: this.i18nService.get('settings.licenseManage_term_activeLicense_refresh_info'),
        NoPermissions_label: this.i18nService.get('common.NoPermissions_label'),
    };

    constructor(
        private httpService: HttpService,
        private i18nService: I18nService,
        private downloadService: DownloadService,
        private promptService: PromptService,
        private globalDataService: GlobalDataService,
        private toolService: ToolService,
        private utilService: UtilService,
    ) { }

    /**
     * 必填错误
     * @param name
     */
    requiredErr(name: string): string {
        return this.i18nService.get('common.null_validation', [name]);
    }

    // 批量查询已激活License
    getBatchActiveLicense(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('license.getActiveBatch');
        return this.httpService.get(rest, options);
    }

    // 批量查询未激活License
    getBatchInactiveLicense(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('license.getInActiveBatch');
        return this.httpService.get(rest, options);
    }

    // 查询已激活license导入路径
    getActiveLicenseInportPath(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('license.getPath');
        return this.httpService.get(rest, options);
    }

    // 查询未激活license状态
    getInactiveLicenseStatus(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('license.getInActLicenseStatus');
        return this.httpService.get(rest, options);
    }

    // 可得容量防呆高危提示
    effectiveCapacitySpecMsg = (licenseCapacity: string, maxCapacity: string): string =>
        this.i18nService.get('settings.licenseManage_task_importLicense_confirm_msg', [licenseCapacity, maxCapacity]);

    /**
     * 获取上传url
     */
    getUploadUrl(): string {
        return this.httpService.makeUrl('license.upload').url;
    }

    // 激活License文件
    activeLinceseFile(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('license.put');
        return this.httpService.put(rest, null, options);
    }

    // 保存License差异文件
    saveLicenseDifferenceFile(): Observable<boolean> {
        const rest = this.httpService.makeUrl('license.saveDiffFile')
            .setParams({
                not_show_used_capacity: this.isSupportLicenseUsage ? SaveDiffShowUsedE.SHOW : SaveDiffShowUsedE.HIDE,
            });
        return this.downloadService.download(rest, 'LicenseCompareReport.html', 'text/html');
    }

    // 备份已激活的License文件
    backupActivatedLicense(path: string, time: string): Observable<boolean> {
        const rest = this.httpService.makeUrl('license.backupActive')
            .setParams({ path });
        return this.downloadService.download(rest, 'ActiveLicenseFile' + time + '.dat');
    }

    // 查询license特性状态
    getLicenseCharacteristicStatus(): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('license.getStatus');
        return this.httpService.get(rest);
    }

    /**
     * 获取License状态
     * @param status
     */
    getLicenseStatus = (status: string): string => this.i18nService.get('common.EnumDefine_CMO_LICENSE_STATUS_' + status);

    /**
     * License特性名称
     * @param featureId 特性ID
     */
    getLicenseFeatureName = (featureId: string): string => {
        let featureName = this.i18nService.get(`common.EnumDefine_LicenseFuncFeatureType_${featureId}`);
        if (this.toolService.isOem()) {
            featureName = featureName.replace(/\s?OceanStor\s?/g, ' ').trim();
        }

        return featureName;
    };

    /**
     * 转换成License特性表格的数据
     * @param data
     */
    adaptToLicenseFeatureData(data: License): LicenseFeatureData[] {
        const featureData: LicenseFeatureData[] = data.LicenseFunction.map((func) => {
            const resource = data.LicenseResource.find((a) => a.FeatureId === func.FeatureId);
            const feature: LicenseFeatureData = {
                featureId: func.FeatureId,
                featureName: this.getLicenseFeatureName(func.FeatureId),
                capacity: UNPLACEHOLDER,
                resourceNum: resource ? Number(resource.ResourceNum) : 0,
                resourceMaxNum: resource ? Number(resource.ResourceSysMaxNum) : 0,
                expirationTime: func.RunTime,
            };

            if (resource) {
                const usageInfo = data.LicenseUsageInfo.find((a) => a.FeatureId === func.FeatureId);
                // 单位为个直接显示无限容量
                if (usageInfo && usageInfo.usedResourceUnit === UsedResourceUnitE.ENTRIES) {
                    feature.capacity = this.i18n.unlimitedText;
                    feature.resourceNum = Number.MAX_SAFE_INTEGER;
                } else {
                    let capacity = UNPLACEHOLDER;

                    if (Number(resource.ResourceNum) === UNLIMITED_CAPACITY) {
                        capacity = this.i18n.unlimitedText;
                    } else if (Number(resource.ResourceNum) !== -1) {
                        capacity = resource.ResourceNum + ' TB';
                    }

                    if (this.isSupportLicenseUsage) {
                        if (usageInfo) {
                            capacity =
                                `${this.utilService.adaptUnit(usageInfo.UsedNum, unitE[usageInfo.usedResourceUnit]).label}/${capacity}`;
                        } else {
                            capacity = `${UNPLACEHOLDER}/${capacity}`;
                        }
                    }

                    feature.capacity = capacity;
                }
            } else {
                feature.capacity = this.i18n.unlimitedText;
                feature.resourceNum = Number.MAX_SAFE_INTEGER;
            }

            return feature;
        });
        return featureData;
    }

    /**
     * 激活License动作
     */
    activeLicenseAction(inactiveFeatureData: LicenseFeatureData[]): any {
        return Observable.create((observer: Subscriber<void>) => {
            let content = this.i18n.activeLicenseMsg;

            // 可得容量超出系统规格后高危提示
            const effectiveCapacity = inactiveFeatureData.find((a) => a.featureId === '104');
            if (effectiveCapacity?.resourceNum &&
                effectiveCapacity?.resourceMaxNum &&
                effectiveCapacity?.resourceNum > effectiveCapacity?.resourceMaxNum
            ) {
                const maxCapacity = `${effectiveCapacity.resourceMaxNum} TB`;
                content = this.effectiveCapacitySpecMsg(effectiveCapacity.capacity, maxCapacity);
            }

            this.promptService.warning({
                id: 'prompt-activeLicenseAction',
                title: this.i18n.activeLicenseBtn,
                content,
                onOk: () => {
                    this.promptService.close();
                    this.activeLinceseFile({ mask: true, doSuccess: true })
                        .subscribe(() => {
                            observer.next();
                            observer.complete();
                            // 激活License之后需要更新License权限信息
                            this.globalDataService.initLicenseFeature().subscribe();
                        });
                },
            });
        });
    }

    /**
     * 是否不显示错误
     * @param code
     */
    isNotShowError(code: number): boolean {
        // 50334237 License文件不存在
        // 1073884674 License文件不完整
        return code === 50334237 || code === 1073884674;
    }

    getLanguage(): string {
        return this.i18nService.language;
    }
}

export const unitE = {
    1: Consts.BYTE,
    2: Consts.KB,
    3: Consts.MB,
    4: Consts.GB,
    5: Consts.TB,
    6: Consts.PB,
};

/**
 * 无线容量数值
 */
export const UNLIMITED_CAPACITY = 2147483647;

/**
 * 已使用容量单位枚举
 */
export enum UsedResourceUnitE {
    NONE = 0,
    BYTE = 1,
    KB = 2,
    MB = 3,
    GB = 4,
    TB = 5,
    PB = 6,
    ENTRIES = 72
}

/**
 * 保存差异对比显示已使用容量参数枚举
 */
export enum SaveDiffShowUsedE {
    SHOW = 0,
    HIDE = 1
}
